import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "react-hot-toast";
import mentorshipService from '@/services/mentorshipService';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function NewCourseModal({ open, onOpenChange, onSuccess }) {
  const [courseName, setCourseName] = useState("");
  const [batchPrefix, setBatchPrefix] = useState("A");
  const [numberOfBatches, setNumberOfBatches] = useState(1);
  const [courseIds, setCourseIds] = useState("");
  const [courseUrl, setCourseUrl] = useState("https://classplusapp.com/diy/courses/course-overview?id=");
  const [batchSize, setBatchSize] = useState(50);
  const [batchNamingFormat, setBatchNamingFormat] = useState("alphabetical"); // "alphabetical" or "numerical"
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedBatchIds, setGeneratedBatchIds] = useState([]);

  useEffect(() => {
    if (open) {
      resetForm();
      setTimeout(() => {
        const firstInput = document.querySelector('#courseName');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    } else {
      setShowConfirmDialog(false);
      setHasChanges(false);
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      setShowConfirmDialog(false);
      setHasChanges(false);
      setIsSubmitting(false);
    };
  }, []);

  useEffect(() => {
    if (batchPrefix && numberOfBatches > 0) {
      let batches;
      
      if (batchNamingFormat === "alphabetical") {
        // Generate alphabetical sequence: A, B, C... Z, AA, AB, etc.
        const generateAlphabeticalSuffix = (index) => {
          // Convert to base-26 representation using alphabets
          let suffix = '';
          let num = index + 1; // +1 because we start with A (index 0)
          
          while (num > 0) {
            num--; // Adjust for 0-based index
            const remainder = num % 26;
            suffix = String.fromCharCode(65 + remainder) + suffix; // 65 is ASCII for 'A'
            num = Math.floor(num / 26);
          }
          
          return suffix || 'A'; // Default to 'A' if empty
        };
        
        batches = Array.from({ length: numberOfBatches }, (_, i) => 
          `${batchPrefix}-${generateAlphabeticalSuffix(i)}`
        );
      } else {
        // Generate numerical sequence: 1, 2, 3, etc.
        batches = Array.from({ length: numberOfBatches }, (_, i) => 
          `${batchPrefix}-${i + 1}`
        );
      }
      
      setGeneratedBatchIds(batches);
    } else {
      setGeneratedBatchIds([]);
    }
  }, [batchPrefix, numberOfBatches, batchNamingFormat]);

  // Validate course IDs to match number of batches
  const validateCourseIds = () => {
    if (!courseIds.trim()) return true; // Empty is valid
    
    const ids = courseIds.split(/[\n,]/).map(id => id.trim()).filter(id => id);
    return ids.length === numberOfBatches;
  };

  const handleInputChange = (e, setter) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  const handleNumberChange = (e, setter) => {
    setter(parseInt(e.target.value) || 1);
    setHasChanges(true);
  };

  const resetForm = () => {
    setCourseName("");
    setBatchPrefix("A");
    setNumberOfBatches(1);
    setCourseIds("");
    setCourseUrl("https://classplusapp.com/diy/courses/course-overview?id=");
    setBatchSize(50);
    setBatchNamingFormat("alphabetical");
    setHasChanges(false);
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      resetForm();
      setShowConfirmDialog(false);
      setHasChanges(false);
      setIsSubmitting(false);
      onOpenChange(false);
    }
  };

  const confirmCancel = () => {
    setShowConfirmDialog(false);
    resetForm();
    setHasChanges(false);
    setIsSubmitting(false);
    onOpenChange(false);
  };

  const handleCreate = async () => {
    try {
      setIsSubmitting(true);
      
      // Use the first batch ID from the generated list
      const firstBatchId = generatedBatchIds.length > 0 ? generatedBatchIds[0] : 
        batchNamingFormat === "alphabetical" ? `${batchPrefix}-A` : `${batchPrefix}-1`;
      
      // Prepare the data for API
      const courseData = {
        courseName,
        batchName: firstBatchId,
        courseIds,
        courseUrl,
        initialCourseCount: batchSize,
        batchPrefix,
        numberOfBatches,
        batchNamingFormat
      };
      
      await mentorshipService.createCourse(courseData);
      toast.success("Course created successfully!");
      
      resetForm();
      setShowConfirmDialog(false);
      setHasChanges(false);
      setIsSubmitting(false);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error creating course:", error);
      toast.error("Failed to create course. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = courseName.trim() !== "" && batchPrefix.trim() !== "" && validateCourseIds();

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Course</DialogTitle>
            <DialogDescription>
              Add a new course to the mentorship program.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="courseName">Course Name*</Label>
              <Input
                id="courseName"
                placeholder="Enter course name"
                value={courseName}
                onChange={(e) => handleInputChange(e, setCourseName)}
                disabled={isSubmitting}
              />
            </div>
            
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="batchPrefix">Batch Prefix*</Label>
                  <Input
                    id="batchPrefix"
                    placeholder="Enter prefix (e.g., Warrior, GS, Cohort)"
                    value={batchPrefix}
                    onChange={(e) => handleInputChange(e, setBatchPrefix)}
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    This will be the prefix for all batches (e.g., {batchPrefix}-A, {batchPrefix}-B)
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="numberOfBatches">Number of Batches*</Label>
                  <Input
                    id="numberOfBatches"
                    type="number"
                    min={1}
                    value={numberOfBatches}
                    onChange={(e) => handleNumberChange(e, setNumberOfBatches)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              {batchNamingFormat === "alphabetical" && numberOfBatches > 26 && (
                <p className="text-xs text-orange-500 font-semibold">
                  Warning: Single-letter alphabetical batches are limited to 26 (A-Z). 
                  Batches beyond 26 will use double letters (AA, AB, etc.)
                </p>
              )}
            </div>
            
            <div className="grid gap-2 border-t border-b py-4 my-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="batchNamingFormat" className="text-md font-semibold">Batch Naming Format</Label>
                  <p className="text-xs text-muted-foreground">
                    Choose how batches will be numbered
                  </p>
                </div>
                <Select 
                  value={batchNamingFormat} 
                  onValueChange={(value) => { 
                    setBatchNamingFormat(value);
                    setHasChanges(true);
                  }}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="batchNamingFormat" className="w-[240px]">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alphabetical">Alphabetical (Prefix-A, Prefix-B...)</SelectItem>
                    <SelectItem value="numerical">Numerical (Prefix-1, Prefix-2...)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-gray-50 p-2 rounded-md mt-2">
                <p className="text-sm">
                  <span className="font-medium">Format: </span>
                  {batchNamingFormat === "alphabetical" ? 
                    `${batchPrefix}-A, ${batchPrefix}-B, ${batchPrefix}-C, ... ${batchPrefix}-Z, ${batchPrefix}-AA, etc.` : 
                    `${batchPrefix}-1, ${batchPrefix}-2, ${batchPrefix}-3, etc.`}
                </p>
              </div>
            </div>
            
            {generatedBatchIds.length > 0 && (
              <div className="grid gap-2">
                <Label className="mb-1">Generated Batch IDs:</Label>
                <div className="bg-gray-100 p-2 rounded-md text-sm max-h-24 overflow-y-auto">
                  {generatedBatchIds.join(', ')}
                </div>
              </div>
            )}
            
            <div className="grid gap-2">
              <Label htmlFor="courseUrl">Course URL</Label>
              <Input
                id="courseUrl"
                placeholder="https://classplusapp.com/diy/courses/course-overview?id="
                value={courseUrl}
                onChange={(e) => handleInputChange(e, setCourseUrl)}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">Base URL for course links</p>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="courseIds">Course IDs (comma-separated)</Label>
              <Input
                id="courseIds"
                placeholder="ID1,ID2,ID3"
                value={courseIds}
                onChange={(e) => handleInputChange(e, setCourseIds)}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Each ID will be mapped to a batch in order (e.g., first ID → first batch). 
                Number of IDs should match number of batches ({numberOfBatches}).
              </p>
              {courseIds && !validateCourseIds() && (
                <p className="text-xs text-red-500">
                  Warning: The number of course IDs ({courseIds.split(/[\n,]/).map(id => id.trim()).filter(id => id).length}) 
                  does not match number of batches ({numberOfBatches}).
                </p>
              )}
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="batchSize">Batch Size</Label>
              <Input
                id="batchSize"
                type="number"
                min={1}
                value={batchSize}
                onChange={(e) => handleNumberChange(e, setBatchSize)}
                disabled={isSubmitting}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!isFormValid || isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Course"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes that will be lost if you close this dialog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmDialog(false)}>
              Continue Editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

NewCourseModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

export default NewCourseModal; 